import styled from '@emotion/styled'
import { AnimatedImage } from 'app/components/Common/Animation/AnimatedImage'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import React, { memo } from 'react'

export interface Props {
  cta?: ButtonProps
  description?: string
  image?: ImageProps
  label?: string
  title?: string
}

export const Item = memo(function Item({
  cta,
  description,
  image,
  label,
  title,
}: Props) {
  return (
    <Container row stretch wrap>
      <LeftSide delay={0.4} duration={1.6}>
        {image ? <Image {...image} /> : null}
      </LeftSide>

      <RightSide dial={4}>
        {label ? <Label tag="h2" text={label} /> : null}

        {title ? <Title tag="h3" text={title} /> : null}

        {description ? (
          <FadeInUp>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}

        {cta ? (
          <FadeInUp>
            <CTA variant="flat" {...cta} />
          </FadeInUp>
        ) : null}
      </RightSide>
    </Container>
  )
})

const LeftSide = styled(AnimatedImage)`
  aspect-ratio: 0.78;
  width: 50%;
  max-height: 47.5rem;
  position: relative;

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    aspect-ratio: 1;
    width: 100%;
    max-height: none;
    margin-bottom: 2rem;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label};

  margin-left: 0;
`

const Title = styled(AnimatedTitle)`
  ${styles.title.medium}

  max-width: 25rem;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Description = styled.div`
  ${styles.description}

  margin-top: 2rem;

  @media (max-width: 1199px) {
    margin-top: 1.5rem;
  }
`

const CTA = styled(Button)`
  margin-top: 2rem;
`

const RightSide = styled(FlexBox)`
  width: 50%;
  padding-left: 9.444vw;

  @media (max-width: 1199px) {
    width: 100%;
    padding-left: 0;
  }
`

const Container = styled(FlexBox)`
  margin-top: 10.5rem;
  &:first-of-type {
    margin-top: 0;
  }
  &:nth-of-type(even) {
    ${LeftSide} {
      order: 2;
    }
    ${RightSide} {
      padding-right: 9.444vw;
      padding-left: 0;
    }
  }

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
    &:nth-of-type(even) {
      ${LeftSide} {
        order: unset;
      }
      ${RightSide} {
        padding-right: 0;
      }
    }
  }
`
